import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusPill, SITE_LABEL } from "@/components/Layout";
import { toast } from "sonner";
import {
  ArrowLeft, Mail, Clock, CalendarDays, TrendingUp, User2, ExternalLink, RefreshCw,
  ArrowUpRight, ArrowDownRight, History, Minus, Bug,
} from "lucide-react";

function StatTile({ label, value, suffix, testid }) {
  return (
    <Card className="border-gray-200">
      <CardContent className="p-5">
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{label}</div>
        <div className="font-heading text-3xl font-bold text-[#1A2744] mt-2" data-testid={testid}>
          {value}
          {suffix && <span className="text-base font-medium text-gray-400 ml-1">{suffix}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
}
function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

const NZD = (v) =>
  (Number(v) || 0).toLocaleString("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 2 });

function RateHistoryCard({ history }) {
  if (!history || history.length === 0) {
    return (
      <Card className="border-gray-200" data-testid="rate-history-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <History className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.8} />
            <h3 className="font-heading text-xl font-bold text-[#1A2744]">Rate-change history</h3>
          </div>
          <p className="text-xs text-gray-500">
            No rate changes recorded yet. Future Xero syncs and admin overrides will be logged here automatically.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-gray-200" data-testid="rate-history-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <History className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.8} />
          <h3 className="font-heading text-xl font-bold text-[#1A2744]">Rate-change history</h3>
          <span className="text-xs text-gray-500">· {history.length} change{history.length === 1 ? "" : "s"}</span>
        </div>
        <ol className="space-y-3 border-l border-gray-200 pl-4">
          {history.map((h) => {
            const isRaise = h.delta != null && h.delta > 0;
            const isCut = h.delta != null && h.delta < 0;
            const isFirst = h.old_rate == null;
            const Icon = isRaise ? ArrowUpRight : isCut ? ArrowDownRight : Minus;
            const color = isRaise ? "text-emerald-600 bg-emerald-50" : isCut ? "text-red-600 bg-red-50" : "text-gray-600 bg-gray-50";
            return (
              <li key={h.id} className="relative" data-testid={`rate-history-row-${h.id}`}>
                <span className={`absolute -left-[22px] top-0.5 inline-flex h-3 w-3 rounded-full border-2 border-white ${isRaise ? "bg-emerald-500" : isCut ? "bg-red-500" : "bg-gray-400"}`} />
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium text-[#1A2744]">
                      {isFirst ? (
                        <>Rate set to <span className="font-mono">{NZD(h.new_rate)}</span></>
                      ) : (
                        <>
                          <span className="font-mono">{NZD(h.old_rate)}</span>{" → "}
                          <span className="font-mono font-semibold">{NZD(h.new_rate)}</span>
                          <span className={`ml-2 inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-mono ${color}`}>
                            <Icon className="h-2.5 w-2.5" />
                            {(h.delta > 0 ? "+" : "") + NZD(h.delta).replace("$", "$")}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {fmtDate(h.effective_at)} · {h.source === "XERO_SYNC" ? "Synced from Xero" : `Manual edit${h.actor_name ? " by " + h.actor_name : ""}`}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

export default function AdminEmployeeDetail() {
  const { id } = useParams();
  const [detail, setDetail] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [rateHistory, setRateHistory] = useState([]);
  const [syncingBalances, setSyncingBalances] = useState(false);
  const [xeroDebug, setXeroDebug] = useState(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const [loadingDebug, setLoadingDebug] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: d1 }, { data: d2 }, { data: d3 }] = await Promise.all([
        api.get(`/admin/users/${id}/detail`, { params: { shifts_limit: 100 } }),
        api.get(`/admin/users/${id}/weekly-summary`, { params: { weeks: 12 } }),
        api.get(`/admin/users/${id}/pay-rate-history`),
      ]);
      setDetail(d1);
      setWeekly(d2);
      setRateHistory(d3.history || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load employee");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const syncBalancesFromXero = async () => {
    setSyncingBalances(true);
    try {
      const { data } = await api.post(`/admin/xero/sync-leave-balances/${id}`);
      if (data.status === "OK") {
        const n = (data.synced || []).length;
        toast.success(`Pulled ${n} balance${n === 1 ? "" : "s"} from Xero.`);
        await load();
      } else if (data.status === "SKIPPED_NO_XERO_EMPLOYEE") {
        toast.message("This employee isn't linked to a Xero employee yet.");
      } else if (data.status === "FAILED") {
        toast.error(data.error || "Xero call failed.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncingBalances(false);
    }
  };

  const loadXeroDebug = async () => {
    setLoadingDebug(true);
    setDebugOpen(true);
    try {
      const { data } = await api.get(`/admin/xero/debug/leave-balances/${id}`);
      setXeroDebug(data);
    } catch (e) {
      setXeroDebug({ error: e?.response?.data?.detail || e.message || "Request failed" });
    } finally {
      setLoadingDebug(false);
    }
  };

  if (!detail || !weekly) {
    return <div className="text-sm text-gray-500">Loading…</div>;
  }

  const u = detail.user;
  const s = detail.summary;

  return (
    <div className="space-y-8" data-testid="admin-employee-detail">
      <div>
        <Link
          to="/admin/employees"
          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#1A2744]"
          data-testid="back-to-employees"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> All employees
        </Link>
      </div>

      {/* Profile header */}
      <Card className="border-gray-200 overflow-hidden">
        <CardContent className="p-0">
          <div className="bg-gradient-to-r from-[#1A2744] to-[#263A63] px-6 py-7 text-white">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="h-14 w-14 rounded-xl bg-[#C9A84C] text-[#1A2744] flex items-center justify-center text-2xl font-bold shadow-lg">
                {u.name?.split(" ").map((w) => w[0]).slice(0, 2).join("") || "?"}
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C] font-semibold">{u.role}</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-1" data-testid="emp-detail-name">{u.name}</h1>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-white/80">
                  {u.employee_code && <span className="font-mono text-[#C9A84C]">{u.employee_code}</span>}
                  <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {u.email}</span>
                  <span>{SITE_LABEL[u.primary_site] || u.primary_site}</span>
                  {u.employment_type && <span>{u.employment_type.replace("_", " ")}</span>}
                  {u.hourly_rate && <span>${u.hourly_rate}/hr</span>}
                  {u.start_date && <span>Joined {fmtDate(u.start_date)}</span>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {u.is_active
                  ? <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/30">Active</Badge>
                  : <Badge className="bg-gray-500/20 text-gray-300 border-gray-400/30">Inactive</Badge>}
                {u.locked_at && <Badge className="bg-red-500/20 text-red-200 border-red-400/30">Locked</Badge>}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile label="This week" value={s.week_hours} suffix="hrs" testid="stat-week" />
        <StatTile label="This month" value={s.month_hours} suffix="hrs" testid="stat-month" />
        <StatTile label="All time" value={s.all_time_hours} suffix="hrs" testid="stat-alltime" />
        <StatTile label="Shifts logged" value={s.shifts_total} testid="stat-shifts" />
      </div>

      {/* Weekly summary */}
      <Card>
        <CardContent className="p-0">
          <div className="p-6 pb-3 flex items-center justify-between">
            <div>
              <h3 className="font-heading text-xl font-bold text-[#1A2744] flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.8} />
                Weekly summary
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">Last 12 weeks — hours, shift counts & approvals</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Week</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead className="text-right">Shifts</TableHead>
                  <TableHead className="text-right">Approved</TableHead>
                  <TableHead className="text-right">Incomplete</TableHead>
                  <TableHead className="text-right">Leave days</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {weekly.weeks.slice().reverse().map((w) => (
                  <TableRow key={w.week_start} data-testid={`week-${w.week_start}`}>
                    <TableCell>
                      <div className="text-sm font-medium text-[#1A2744]">{fmtDate(w.week_start)}</div>
                      <div className="text-[11px] text-gray-500">– {fmtDate(w.week_end)}</div>
                    </TableCell>
                    <TableCell className="text-right font-heading text-base font-bold text-[#1A2744]">
                      {w.hours}
                      <span className="text-[11px] text-gray-400 ml-1 font-normal">hrs</span>
                    </TableCell>
                    <TableCell className="text-right">{w.shifts || "—"}</TableCell>
                    <TableCell className="text-right text-emerald-600">{w.approved_shifts || "—"}</TableCell>
                    <TableCell className={`text-right ${w.incomplete_shifts ? "text-amber-600 font-semibold" : ""}`}>
                      {w.incomplete_shifts || "—"}
                    </TableCell>
                    <TableCell className="text-right">{w.leave_days || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Shifts + Leave side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-3">
          <CardContent className="p-0">
            <div className="p-6 pb-3">
              <h3 className="font-heading text-xl font-bold text-[#1A2744] flex items-center gap-2">
                <Clock className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.8} /> Recent shifts
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">{detail.shifts.length} most recent · shifts over 6h auto-deduct a 30-min meal break</p>
            </div>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white">
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>In</TableHead>
                    <TableHead>Out</TableHead>
                    <TableHead className="text-right">Hrs</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sync</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.shifts.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-8">No shifts yet.</TableCell></TableRow>
                  )}
                  {detail.shifts.map((sh) => (
                    <TableRow key={sh.id} data-testid={`shift-${sh.id}`}>
                      <TableCell className="whitespace-nowrap">{fmtDate(sh.clock_in_ts)}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtTime(sh.clock_in_ts)}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtTime(sh.clock_out_ts)}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {sh.total_minutes ? (sh.total_minutes / 60).toFixed(2) : "—"}
                        {sh.break_minutes > 0 && (
                          <span
                            className="ml-1 text-[10px] px-1 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 align-middle"
                            title={`Auto-deducted ${sh.break_minutes} min break`}
                          >
                            -{sh.break_minutes}m
                          </span>
                        )}
                      </TableCell>
                      <TableCell><StatusPill status={sh.status} /></TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5 items-start">
                          <StatusPill status={sh.xero_sync_status || "NONE"} />
                          {sh.xero_timesheet_url && (
                            <a
                              href={sh.xero_timesheet_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] text-[#C9A84C] hover:text-[#1A2744] hover:underline"
                              data-testid={`detail-xero-link-${sh.id}`}
                            >
                              <ExternalLink className="h-3 w-3" /> Xero
                            </a>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardContent className="p-0">
            <div className="p-6 pb-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-heading text-xl font-bold text-[#1A2744] flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.8} /> Leave
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">Balances & requests</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadXeroDebug}
                    data-testid="emp-detail-debug-xero"
                    className="gap-1.5 text-xs"
                    title="See the raw Xero leaveBalances response and how each entry was classified"
                  >
                    <Bug className="h-3 w-3" /> Debug Xero
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={syncBalancesFromXero}
                    disabled={syncingBalances}
                    data-testid="emp-detail-sync-balances"
                    className="gap-1.5 text-xs"
                  >
                    <RefreshCw className={`h-3 w-3 ${syncingBalances ? "animate-spin" : ""}`} />
                    {syncingBalances ? "Syncing…" : "Sync from Xero"}
                  </Button>
                </div>
              </div>
            </div>
            <div className="px-6 pb-4 space-y-3">
              {detail.leave_balances.length === 0 && (
                <p className="text-xs text-gray-500">No leave balances set.</p>
              )}
              {detail.leave_balances.map((b) => {
                const remaining = (b.entitled_days || 0) - (b.used_days || 0);
                const liability = b.liability_value || 0;
                return (
                  <div key={b.leave_type} className="flex items-center justify-between text-sm" data-testid={`balance-${b.leave_type}`}>
                    <span className="font-medium text-[#1A2744] capitalize">
                      {b.leave_type.replace("_", " ").toLowerCase()}
                    </span>
                    <div className="flex items-baseline gap-3">
                      {b.is_liable && (
                        <span
                          className="font-mono text-xs text-gray-600"
                          title="Projected $ paid out on termination (NZ Holidays Act)"
                          data-testid={`balance-liability-${b.leave_type}`}
                        >
                          {liability.toLocaleString("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 2 })}
                        </span>
                      )}
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-heading text-lg font-bold text-emerald-600">{remaining.toFixed(1)}</span>
                        <span className="text-[11px] text-gray-400">hrs · of {(b.entitled_days || 0).toFixed(1)} hrs</span>
                      </div>
                    </div>
                  </div>
                );
              })}
              {detail.leave_liability && detail.leave_liability.total > 0 && (
                <div
                  className="flex items-center justify-between border-t border-gray-100 pt-3 text-sm"
                  data-testid="emp-leave-liability-total"
                >
                  <span className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
                    Total leave liability
                  </span>
                  <span className="font-mono font-bold text-[#1A2744]">
                    {detail.leave_liability.total.toLocaleString("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              {detail.leave_liability && detail.leave_liability.total === 0 && !detail.leave_liability.hourly_rate && (
                <div
                  className="text-[11px] text-amber-700 border-t border-gray-100 pt-3"
                  data-testid="emp-leave-liability-no-rate"
                >
                  Set an hourly rate to project this employee&apos;s leave liability.
                </div>
              )}
            </div>
            <div className="px-6 pb-2 mt-1 border-t border-gray-100 pt-3">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">Requests</div>
            </div>
            <div className="max-h-[320px] overflow-y-auto">
              {detail.leave_requests.length === 0 && (
                <p className="px-6 py-4 text-xs text-gray-500">No leave requests yet.</p>
              )}
              {detail.leave_requests.map((r) => (
                <div key={r.id} className="px-6 py-2.5 border-t border-gray-100 text-xs flex items-center justify-between gap-3" data-testid={`leave-${r.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-[#1A2744] capitalize">{r.leave_type.replace("_", " ").toLowerCase()}</div>
                    <div className="text-[11px] text-gray-500 truncate">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</div>
                  </div>
                  <StatusPill status={r.status} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rate-change history timeline */}
      <RateHistoryCard history={rateHistory} />

      {/* Xero raw-data debug dialog (admin troubleshooting) */}
      <Dialog open={debugOpen} onOpenChange={setDebugOpen}>
        <DialogContent className="max-w-3xl" data-testid="xero-debug-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-4 w-4 text-[#C9A84C]" /> Xero leave-balance diagnostics
            </DialogTitle>
          </DialogHeader>
          {loadingDebug ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : xeroDebug?.error ? (
            <div className="text-sm text-red-600 break-words">{xeroDebug.error}</div>
          ) : xeroDebug ? (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="text-xs text-gray-500">
                Status: <span className="font-mono text-[#1A2744]">{xeroDebug.status}</span>
                {" · "}Xero employee: <span className="font-mono">{xeroDebug.xero_employee_id || "—"}</span>
                {" · "}Raw count: <span className="font-mono">{xeroDebug.raw_count ?? "—"}</span>
              </div>
              {xeroDebug.classified && xeroDebug.classified.length > 0 ? (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1">
                    Classification
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Xero name</TableHead>
                        <TableHead className="text-right">Units</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Mapped to</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {xeroDebug.classified.map((c, i) => (
                        <TableRow key={c.id || `${c.name || "row"}-${i}`}>
                          <TableCell className="font-mono text-xs">{c.name || "—"}</TableCell>
                          <TableCell className="text-right font-mono text-xs">{c.units}</TableCell>
                          <TableCell className="font-mono text-xs">{c.type_of_units}</TableCell>
                          <TableCell>
                            {c.matched_to ? (
                              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                                {c.matched_to}
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                                UNMAPPED
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-amber-700">
                  Xero returned 0 leave-balance entries. The employee likely has no leave types
                  assigned in Xero NZ Payroll yet (Payroll → Employees → click employee → Leave tab).
                </p>
              )}
              {/* Pay-template diagnostic — explains $0 leave-liability cases. */}
              {xeroDebug.pay_template && (
                <div className="border-t border-gray-100 pt-3">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1">
                    Hourly rate from Xero PayTemplate
                  </div>
                  <div className="text-xs text-gray-600 mb-2">
                    Status: <span className="font-mono text-[#1A2744]">{xeroDebug.pay_template.status}</span>
                    {" · "}Extracted rate: {" "}
                    {xeroDebug.pay_template.extracted_rate != null ? (
                      <span className="font-mono font-bold text-emerald-700">${xeroDebug.pay_template.extracted_rate}</span>
                    ) : (
                      <span className="font-mono text-red-600">null</span>
                    )}
                    {" · "}Current DB rate: {" "}
                    {xeroDebug.pay_template.current_user_hourly_rate != null ? (
                      <span className="font-mono font-bold text-[#1A2744]">${xeroDebug.pay_template.current_user_hourly_rate}</span>
                    ) : (
                      <span className="font-mono text-amber-700">null</span>
                    )}
                  </div>
                  {xeroDebug.pay_template.error && (
                    <div className="text-sm text-red-600 mb-2">Error: {xeroDebug.pay_template.error}</div>
                  )}
                  {xeroDebug.pay_template.extracted_rate == null && (
                    <p className="text-xs text-amber-700 mb-2">
                      ⚠️ The PayTemplate parser couldn&apos;t extract a rate from this response. Send the raw JSON below
                      to support so we can teach the parser the shape your tenant returns.
                    </p>
                  )}
                  <details>
                    <summary className="text-xs text-gray-500 cursor-pointer hover:text-[#1A2744]">Raw SalaryAndWages JSON ({xeroDebug.pay_template.salary_and_wages_status})</summary>
                    <pre className="mt-2 text-[10px] font-mono bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto" data-testid="salary-wages-raw">
                      {JSON.stringify(xeroDebug.pay_template.salary_and_wages_raw, null, 2)}
                    </pre>
                  </details>
                  <details>
                    <summary className="text-xs text-gray-500 cursor-pointer hover:text-[#1A2744]">Raw PayTemplate JSON</summary>
                    <pre className="mt-2 text-[10px] font-mono bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto" data-testid="paytemplate-raw">
                      {JSON.stringify(xeroDebug.pay_template.raw, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
              <details>
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-[#1A2744]">Raw JSON response</summary>
                <pre className="mt-2 text-[10px] font-mono bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto">
                  {JSON.stringify(xeroDebug.raw, null, 2)}
                </pre>
              </details>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Quick links to other admin actions */}
      <div className="flex gap-3 flex-wrap">
        <Link to="/admin/employees">
          <Button variant="outline" data-testid="link-to-employees"><User2 className="h-4 w-4 mr-1" /> Back to employees</Button>
        </Link>
        <Link to={`/admin/timesheets`}>
          <Button variant="outline" data-testid="link-to-timesheets"><Clock className="h-4 w-4 mr-1" /> All timesheets</Button>
        </Link>
        <Link to={`/admin/variance`}>
          <Button variant="outline" data-testid="link-to-variance"><TrendingUp className="h-4 w-4 mr-1" /> Hours variance</Button>
        </Link>
      </div>
    </div>
  );
}
